import { useEffect, useState } from "react";
import api from "../lib/api";
import { Award, CheckCircle2, XCircle, PauseCircle, Sparkles } from "lucide-react";

const REC_LABEL = {
  strong_hire: { label: "Strong Hire", color: "bg-emerald-600 text-white" },
  hire: { label: "Hire", color: "bg-emerald-500 text-white" },
  review: { label: "Review", color: "bg-amber-500 text-white" },
  not_recommended: { label: "Not Recommended", color: "bg-red-500 text-white" },
};

const OUTCOME_ICON = {
  selected: { icon: CheckCircle2, color: "text-emerald-600" },
  rejected: { icon: XCircle, color: "text-red-600" },
  hold: { icon: PauseCircle, color: "text-amber-500" },
};

function scoreBar(v, label) {
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest">
        <span className="text-muted-foreground font-bold">{label}</span>
        <span className="font-bold text-[#0A251D]">{v ?? "—"}{v ? "/100" : ""}</span>
      </div>
      <div className="h-1.5 bg-secondary rounded-full mt-1 overflow-hidden">
        <div className="h-full bg-[#0A251D]" style={{ width: `${Math.min(v || 0, 100)}%` }} />
      </div>
    </div>
  );
}

export default function FinalAssessment({ candidateId }) {
  const [fa, setFa] = useState(null);
  useEffect(() => {
    if (!candidateId) return;
    api.get(`/candidates/${candidateId}/final-assessment`).then((r) => setFa(r.data)).catch(() => setFa(null));
  }, [candidateId]);

  if (!fa) return null;
  const rec = REC_LABEL[fa.final_recommendation] || REC_LABEL.review;

  return (
    <div className="border-2 border-[#0A251D] bg-white rounded-xl p-6" data-testid="final-assessment-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Award size={22} className="text-[#0A251D]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#E05A47] font-bold">PRD §16</div>
            <div className="font-heading font-black text-xl leading-tight">Consolidated Final Assessment</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading font-black text-4xl text-[#0A251D]" data-testid="fa-weighted-score">{fa.weighted_score}</div>
          <span className={`inline-block mt-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded font-bold ${rec.color}`}>
            AI: {rec.label}
          </span>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-5" data-testid="fa-signals">
        <div className="border-t pt-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-2 flex items-center gap-1"><Sparkles size={11} /> Profile Match</div>
          {scoreBar(fa.profile_match.overall_score, "Overall")}
          <div className="mt-2 space-y-1.5">
            {scoreBar(fa.profile_match.skills_match, "Skills")}
            {scoreBar(fa.profile_match.experience_match, "Experience")}
            {scoreBar(fa.profile_match.education_match, "Education")}
          </div>
          {fa.profile_match.summary && <div className="text-xs mt-2 text-muted-foreground italic">&ldquo;{fa.profile_match.summary}&rdquo;</div>}
        </div>

        <div className="border-t pt-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-2">AI IVR Screening</div>
          {fa.ivr.score ? (
            <>
              {scoreBar(fa.ivr.score, "IVR Overall")}
              <div className="text-xs mt-2 capitalize"><span className="text-muted-foreground">Recommendation:</span> <span className="font-bold">{fa.ivr.recommendation?.replace(/_/g, " ")}</span></div>
              {fa.ivr.summary && <div className="text-xs mt-1 text-muted-foreground italic">&ldquo;{fa.ivr.summary}&rdquo;</div>}
              {fa.ivr.recording_available && (
                <div className="mt-2 text-xs inline-flex items-center gap-1.5 border border-[#0A251D] px-2 py-1 rounded" data-testid="fa-ivr-recording">
                  <span className="w-1.5 h-1.5 bg-[#E05A47] rounded-full animate-pulse" /> Call recording available <span className="text-muted-foreground">(simulated)</span>
                </div>
              )}
            </>
          ) : <div className="text-xs text-muted-foreground">No IVR call yet — trigger it from the IVR panel.</div>}
        </div>

        <div className="border-t pt-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-2">Weights &amp; Ranking</div>
          <div className="text-xs space-y-1">
            {Object.entries(fa.weights).map(([k, v]) => (
              <div key={k} className="flex justify-between"><span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span><span className="font-bold">{v}%</span></div>
            ))}
          </div>
          <div className="mt-3 text-xs"><span className="text-muted-foreground">Bucket:</span> <span className="font-bold text-[#0A251D]">Priority {fa.priority}</span></div>
        </div>
      </div>

      {fa.interview_rounds.length > 0 && (
        <div className="border-t mt-5 pt-4" data-testid="fa-rounds">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">Interview rounds ({fa.interview_rounds.length})</div>
          <div className="grid md:grid-cols-2 gap-3">
            {fa.interview_rounds.map((r, i) => (
              <div key={i} className="border rounded-lg p-3 text-sm">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{r.round_order}. {r.round_name}</div>
                  {r.overall_rating && <span className="font-bold text-[#0A251D]">{r.overall_rating}/5</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 capitalize">{r.status} · {r.panelists?.join(", ") || "—"}</div>
                {r.recommendation && <div className="text-[10px] uppercase tracking-wider mt-1.5 font-bold capitalize" style={{ color: r.recommendation.includes("hire") ? "#0A7A54" : "#B91C1C" }}>{r.recommendation.replace(/_/g, " ")}</div>}
                {r.teams_notes_present && <div className="text-[10px] text-[#E05A47] font-bold mt-1">↳ Teams AI Note-Taker used</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {fa.external_decision && (
        <div className="border-t mt-5 pt-4" data-testid="fa-external">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-2">External / Final Round</div>
          <div className="flex items-center gap-3">
            {(() => { const O = OUTCOME_ICON[fa.external_decision.status]; return O ? <O.icon size={22} className={O.color} /> : null; })()}
            <div>
              <div className="text-sm font-semibold capitalize">{fa.external_decision.status?.replace(/_/g, " ") || "Pending"}</div>
              <div className="text-xs text-muted-foreground">Rating {fa.external_decision.rating || "—"}/5 · by {fa.external_decision.interviewer_name}</div>
              {fa.external_decision.remarks && <div className="text-xs italic mt-1">&ldquo;{fa.external_decision.remarks}&rdquo;</div>}
            </div>
          </div>
        </div>
      )}

      {fa.offer && (
        <div className="border-t mt-5 pt-4" data-testid="fa-offer">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-2">Offer</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-muted-foreground">CTC:</span> <span className="font-bold">₹{fa.offer.ctc_lpa} LPA</span></div>
            <div><span className="text-muted-foreground">Joining:</span> <span className="font-bold">{fa.offer.joining_date}</span></div>
            <div><span className="text-muted-foreground">Status:</span> <span className="font-bold capitalize">{fa.offer.status}</span></div>
            <div><span className="text-muted-foreground">Negotiation rounds:</span> <span className="font-bold">{fa.offer.negotiation_rounds}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
